import { useCallback, useMemo, useState } from 'react'
import {
  type DashboardData,
  type DashboardDataWithEditable,
  type DashboardId,
  type DashboardWidget,
  type ReportDateRange,
} from '@terros/common'
import {
  getDashboardFeedGroups,
  getFeedSectionDateRange,
  getHustleDashboards,
  getVisibleDashboardWidgets,
  useDashboards,
  useProfile,
  type DashboardFeedGroup,
  type GetHookResponse,
} from '@front/model'

export type HomeDashboardFeedState = Pick<
  GetHookResponse<DashboardDataWithEditable[]>,
  'error' | 'isFetching' | 'isLoading' | 'refetch'
> & {
  groups: DashboardFeedGroup<DashboardDataWithEditable>[]
  /** The range explicitly picked at the page level, applying to every section. */
  homeDateRange: ReportDateRange | undefined
  hustleDashboard: DashboardDataWithEditable | undefined
  hustleWidgets: DashboardWidget[]
  /** The ranges explicitly picked on each section, keyed by dashboard. */
  sectionSelections: Map<DashboardId, ReportDateRange>
  selectHomeDateRange: (range: ReportDateRange) => void
  clearHomeDateRange: () => void
  selectSectionDateRange: (dashboardId: DashboardId, range: ReportDateRange) => void
  clearSectionDateRange: (dashboardId: DashboardId) => void
  /** The baseline a section's inheriting widgets query: its pick, then the page-level pick, then the dashboard's range, then the default. */
  sectionDateRange: (dashboard: DashboardData) => ReportDateRange
}

/**
 * The Home feed's dashboards, split for display, plus the client-side date
 * range selections. A section's picker overrides the page-level pick for its
 * dashboard alone; picking a page-level range resets the section picks so it
 * applies everywhere. Widgets with their own configured range ignore both.
 */
export function useHomeDashboardFeed(): HomeDashboardFeedState {
  const { user, hasPermission, isLoading: isProfileLoading } = useProfile()
  const canList = !isProfileLoading && hasPermission('dashboard:list')
  const { data: dashboards, isLoading, error, isFetching, refetch } = useDashboards({ enabled: canList })
  const [homeDateRange, setHomeDateRange] = useState<ReportDateRange | undefined>(undefined)
  const [sectionSelections, setSectionSelections] = useState<Map<DashboardId, ReportDateRange>>(() => new Map())

  const hustleDashboard = useMemo(() => {
    const hustle = getHustleDashboards({
      dashboards: dashboards ?? [],
      scopeEntityId: user?.userId,
      viewerUserId: user?.userId,
    })
    return dashboards?.find((dashboard) => dashboard.dashboardId === hustle.surfacedDashboardId)
  }, [dashboards, user?.userId])

  const hustleWidgets = useMemo(
    () => (hustleDashboard ? getVisibleDashboardWidgets(hustleDashboard) : []),
    [hustleDashboard]
  )

  const groups = useMemo(() => getDashboardFeedGroups(dashboards ?? []), [dashboards])

  const selectHomeDateRange = useCallback((range: ReportDateRange): void => {
    setHomeDateRange(range)
    setSectionSelections(new Map())
  }, [])

  const clearHomeDateRange = useCallback((): void => {
    setHomeDateRange(undefined)
  }, [])

  const selectSectionDateRange = useCallback((dashboardId: DashboardId, range: ReportDateRange): void => {
    setSectionSelections((current) => new Map(current).set(dashboardId, range))
  }, [])

  const clearSectionDateRange = useCallback((dashboardId: DashboardId): void => {
    setSectionSelections((current) => {
      const next = new Map(current)
      next.delete(dashboardId)
      return next
    })
  }, [])

  const sectionDateRange = useCallback(
    (dashboard: DashboardData): ReportDateRange =>
      getFeedSectionDateRange({
        sectionDateRange: sectionSelections.get(dashboard.dashboardId),
        homeDateRange,
        dashboardDateRange: dashboard.dateRange,
      }),
    [homeDateRange, sectionSelections]
  )

  return {
    error,
    groups,
    homeDateRange,
    hustleDashboard,
    hustleWidgets,
    isFetching,
    isLoading,
    refetch,
    sectionDateRange,
    sectionSelections,
    selectHomeDateRange,
    clearHomeDateRange,
    selectSectionDateRange,
    clearSectionDateRange,
  }
}
